import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  tokenAtom,
  rolesAtom,
  scopesAtom,
  featuresAtom,
  uidAtom,
  emailAtom,
  isAuthenticatedAtom,
  mfaClaimAtom,
  orgRoleAtom,
  orgIdAtom,
} from "../store/authAtoms";
import apiClient from "../api/apiClient";
import { MfaClaim, OrgFeature, PageScope, UserRole } from "../utils/types";
import {
  persistToken,
  persistUid,
  persistRoles,
  persistScopes,
  persistFeatures,
  persistEmail,
  persistOrgRole,
  persistOrgId,
  extractScopes,
  extractOrgRole,
  extractOrgId,
  extractFeatures,
} from "../utils/authStorage";
import {
  authOrganizationKeys,
  type SwitchOrgResponse,
} from "../api/queries/authOrganizationsQueries";

export const useAuth = () => {
  const token = useAtomValue(tokenAtom);
  const roles = useAtomValue(rolesAtom);
  const scopes = useAtomValue(scopesAtom);
  const features = useAtomValue(featuresAtom);
  const uid = useAtomValue(uidAtom);
  const email = useAtomValue(emailAtom);
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const mfaClaim = useAtomValue(mfaClaimAtom);
  const orgRole = useAtomValue(orgRoleAtom);
  const orgId = useAtomValue(orgIdAtom);
  const queryClient = useQueryClient();

  const setToken = useSetAtom(tokenAtom);
  const setRoles = useSetAtom(rolesAtom);
  const setScopes = useSetAtom(scopesAtom);
  const setFeatures = useSetAtom(featuresAtom);
  const setUid = useSetAtom(uidAtom);
  const setEmail = useSetAtom(emailAtom);
  const setMfaClaim = useSetAtom(mfaClaimAtom);
  const setOrgRole = useSetAtom(orgRoleAtom);
  const setOrgId = useSetAtom(orgIdAtom);

  const isOrgManager = orgRole === "MANAGER";
  const isOrgCollector = orgRole === "COLLECTOR";
  const isOrgViewer = orgRole === "VIEWER";
  const canMutateOrg = isOrgManager || isOrgCollector;

  const hasFeature = useCallback(
    (feature: OrgFeature) => (features ?? []).includes(feature),
    [features],
  );

  const login = useCallback(
    async (accessToken: string, refreshToken?: string) => {
      try {
        const payload = JSON.parse(atob(accessToken.split(".")[1])) as Record<
          string,
          unknown
        >;
        const userId = payload.user_id;
        const userRoles = payload.roles || ["USER"];
        const userEmail = (payload.email as string) || null;
        const mfa: MfaClaim = (payload.mfa as MfaClaim) || "not_required";
        const pageScopes = extractScopes(payload);
        const orgFeatures = extractFeatures(payload);
        const nextOrgRole = extractOrgRole(payload);
        const nextOrgId = extractOrgId(payload);

        persistToken(accessToken);
        if (refreshToken) {
          localStorage.setItem("refresh_token", refreshToken);
        }
        persistUid(userId?.toString() || null);
        persistRoles(userRoles as UserRole[]);
        persistScopes(pageScopes);
        persistFeatures(orgFeatures);
        persistOrgRole(nextOrgRole);
        persistOrgId(nextOrgId);
        if (userEmail) {
          persistEmail(userEmail);
        }

        setToken(accessToken);
        setRoles(userRoles as UserRole[]);
        setScopes(pageScopes);
        setFeatures(orgFeatures);
        setUid(userId?.toString() || null);
        setEmail(userEmail);
        setMfaClaim(mfa);
        setOrgRole(nextOrgRole);
        setOrgId(nextOrgId);

        // Skip backend roles fetch when MFA is pending (would get 403)
        if (mfa !== "pending") {
          try {
            const { data } = await apiClient.get<{ roles: UserRole }>(
              "/user/roles",
            );
            if (data?.roles) {
              persistRoles([data.roles]);
              setRoles([data.roles]);
            }
          } catch (error) {
            if (import.meta.env.DEV) {
              console.warn(
                "Falling back to token roles, backend roles fetch failed",
                error,
              );
            }
          }
          try {
            const { data } = await apiClient.get<{ page_scopes: PageScope[] }>(
              "/user/page_scopes",
            );
            if (data?.page_scopes?.length) {
              persistScopes(data.page_scopes);
              setScopes(data.page_scopes);
            }
          } catch {
            /* keep JWT scopes */
          }
          void queryClient.invalidateQueries({
            queryKey: authOrganizationKeys.all,
          });
        }
      } catch (error) {
        console.error("Failed to decode token:", error);
        throw error;
      }
    },
    [
      queryClient,
      setRoles,
      setScopes,
      setFeatures,
      setToken,
      setUid,
      setEmail,
      setMfaClaim,
      setOrgRole,
      setOrgId,
    ],
  );

  const switchOrg = useCallback(
    async (targetOrgId: number) => {
      const { data } = await apiClient.post<SwitchOrgResponse>(
        "/auth/switch-org",
        { org_id: targetOrgId },
      );
      await login(data.access_token);
      // Drop cached org-scoped data so the next render loads the new org.
      await queryClient.invalidateQueries();
      return data;
    },
    [login, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      console.error("Logout API call failed:", error);
    }

    persistToken(null);
    persistUid(null);
    persistRoles(null);
    persistScopes(null);
    persistFeatures(null);
    persistEmail(null);
    persistOrgRole(null);
    persistOrgId(null);
    localStorage.removeItem("refresh_token");
    setToken(null);
    setRoles(null);
    setScopes(null);
    setFeatures(null);
    setUid(null);
    setEmail(null);
    setMfaClaim(null);
    setOrgRole(null);
    setOrgId(null);
    queryClient.clear();
  }, [
    queryClient,
    setRoles,
    setScopes,
    setFeatures,
    setToken,
    setUid,
    setEmail,
    setMfaClaim,
    setOrgRole,
    setOrgId,
  ]);

  return {
    token,
    roles,
    scopes,
    features,
    uid,
    email,
    orgId,
    isAuthenticated,
    mfaClaim,
    orgRole,
    isOrgManager,
    isOrgCollector,
    isOrgViewer,
    canMutateOrg,
    hasFeature,
    login,
    logout,
    switchOrg,
  };
};
